class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,tag'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  fetchSkuFallback(query) {
    const url = new URL(`${window.shopUrl}/search/suggest.json`);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', 5);
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');

    fetch(url)
      .then(res => res.json())
      .then(data => {
        const products = data?.resources?.results?.products || data?.resources?.products || [];
        if (!products.length) return;
        this.renderSkuFallback(products);
      })
      .catch(err => console.error('SKU fallback error:', err));
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.isSkuQuery = false;
      this.clear();
      return;
    }
    const looksLikeSKU = /^[A-Z0-9-_]{5,}$/i.test(query);
    this.isSkuQuery = looksLikeSKU;
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(responseText, 'text/html');
        const query = this.getQuery();
        const noResultsText = parsedDoc.body.innerText.includes('No results');

        if (this.isSkuQuery && noResultsText) {
          this.fetchSkuFallback(query);
          this.setLoadingState(false);
          return;
        }

        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);
    if (!targetElement) return;

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);
    if (!contentElement) return;

    this.searchContent?.classList.remove('hidden');
    targetElement.innerHTML = contentElement.innerHTML;

    if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(true);
    }
    this.classList.add('predictive-search-open');
  }

  renderSkuFallback(products) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);
    if (!targetElement) return;

    this.searchContent?.classList.remove('hidden');

    targetElement.innerHTML = `
      <div class="flex w-full flex-col gap-y-6 md:flex-row">
        <div class="flex flex-col flex-grow search__box-products order-last md:order-first">
          <div class="flex flex-col gap-5 lg:gap-6 predictive-search-result search__box-item predictive-search-result--products">
            <h4 class="h4 predictive-search-result__heading">Products</h4>
            <div class="swipe-mobile swipe-mobile--2-cols">
              <ul class="f-grid swipe-mobile__inner" style="list-style:none;margin:0;padding:0;">
                ${products.map(product => {
                  const img = product.image || product.featured_image;
                  const imgUrl = typeof img === 'string' ? img : img?.src || '';
                  const variant = product.variants?.[0];
                  const sizedImg = imgUrl ? imgUrl.replace(/\.jpg|\.png|\.webp/i, (ext) => `_160x160${ext}`) : '';

                  return `
                    <li class="f-column">
                      <a href="${product.url}" style="display:flex;flex-direction:column;gap:0.5rem;text-decoration:none;color:inherit;">
                        ${sizedImg ? `
                          <div style="aspect-ratio:1/1;overflow:hidden;border-radius:var(--blocks-radius,4px);background:#f6f6f6;">
                            <img src="${sizedImg}" alt="${product.title}" style="width:100%;height:100%;object-fit:cover;" loading="lazy">
                          </div>
                        ` : `
                          <div style="aspect-ratio:1/1;background:#f6f6f6;border-radius:var(--blocks-radius,4px);"></div>
                        `}
                        <div>
                          <div style="font-size:0.875rem;font-weight:500;">${product.title}</div>
                          ${variant?.sku ? `<div style="font-size:0.75rem;color:#888;">SKU: ${variant.sku}</div>` : ''}
                        </div>
                      </a>
                    </li>
                  `;
                }).join('')}
              </ul>
            </div>
          </div>
        </div>
      </div>
    `;

    this.classList.add('predictive-search-open');
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
